/**
 * Deduplication Cache for Image Assets
 *
 * Provides a shared cache for content-based deduplication across all retrieve tools.
 * This ensures that the same image content is only stored once, regardless of which
 * tool retrieves it first.
 *
 * Usage:
 * 1. Before storing a new asset, call findExistingAsset(contentHash)
 * 2. If it returns an assetId, use that instead of storing a new asset
 * 3. After storing a new asset, call registerContentHash(contentHash, assetId)
 */
import { simpleHash, contentHash } from './hashUtils';
// ═══════════════════════════════════════════════════════════════════════════
// SHARED DEDUPLICATION CACHE
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Single shared cache for content hashes across all retrieve tools.
 * Maps content hash -> asset ID
 *
 * This enables cross-tool deduplication:
 * - Same image retrieved via Brandfetch and Wikimedia → stored once
 * - Same image retrieved multiple times → returns existing asset ID
 */
const contentHashCache = new Map();
/**
 * Find an existing asset by content hash.
 *
 * @param hash - Content hash to look up
 * @returns Existing asset ID if found, undefined otherwise
 */
export function findExistingAsset(hash) {
    return contentHashCache.get(hash);
}
/**
 * Register a content hash -> asset ID mapping.
 * Call this after storing a new asset to enable future deduplication.
 *
 * @param hash - Content hash of the asset
 * @param assetId - ID of the stored asset
 */
export function registerContentHash(hash, assetId) {
    contentHashCache.set(hash, assetId);
    console.log(`[DeduplicationCache] Registered hash for asset: ${assetId}`);
}
/**
 * Clear the deduplication cache.
 * Typically used for testing or session reset.
 */
export function clearDeduplicationCache() {
    const size = contentHashCache.size;
    contentHashCache.clear();
    console.log(`[DeduplicationCache] Cleared ${size} entries`);
}
/**
 * Get the current size of the deduplication cache.
 */
export function getDeduplicationCacheSize() {
    return contentHashCache.size;
}
// Re-export hash functions for convenience
export { simpleHash, contentHash };
